import React from "react";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";

export function StripeWarningBanner({ payoutsEnabled, onConnect }) {
  if (payoutsEnabled) return null;
  return (
    <div
      data-testid="stripe-warning-banner"
      className="mb-8 rounded-sm border border-amber-300 bg-amber-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="flex items-start gap-3 flex-1">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-semibold text-amber-900">Your store isn’t ready yet.</div>
          <div className="text-sm text-amber-800">Connect Stripe to receive payouts and become eligible to sell.</div>
        </div>
      </div>
      <button
        onClick={onConnect}
        data-testid="banner-connect-stripe-btn"
        className="btn-primary px-5 py-2.5 text-sm rounded-sm whitespace-nowrap"
      >
        Connect Stripe
      </button>
    </div>
  );
}

export function SellerOnboarding({ user, productCount, onNavigate, onAddProduct }) {
  const shippingConfigured = !!(
    Number(user?.shipping_domestic) > 0 ||
    Number(user?.free_shipping_threshold) > 0 ||
    user?.ships_internationally
  );

  const tasks = [
    { key: "name", label: "Store name", desc: "Add your shop / business name.", done: !!user?.business_name, go: () => onNavigate("profile", "task-name") },
    { key: "description", label: "Store description", desc: "Write a short shop bio.", done: !!user?.bio, go: () => onNavigate("profile", "task-desc") },
    { key: "logo", label: "Logo", desc: "Upload your shop logo.", done: !!user?.logo, go: () => onNavigate("profile", "task-logo") },
    { key: "banner", label: "Banner image", desc: "Upload a shop banner.", done: !!user?.banner, go: () => onNavigate("profile", "task-banner") },
    { key: "shipping", label: "Shipping settings", desc: "Set your shipping rates.", done: shippingConfigured, go: () => onNavigate("profile", "task-shipping") },
    { key: "stripe", label: "Connect Stripe", desc: "Connect Stripe to receive weekly payouts.", done: !!user?.stripe_payouts_enabled, go: () => onNavigate("payouts", null) },
    { key: "product", label: "Add your first product", desc: "Upload photos, pricing and descriptions.", done: productCount > 0, go: onAddProduct },
  ];

  const total = tasks.length;
  const completed = tasks.filter((t) => t.done).length;
  const pct = Math.round((completed / total) * 100);
  if (completed === total) return null;

  return (
    <div data-testid="seller-onboarding-card" className="mb-10 border border-border rounded-sm bg-card overflow-hidden">
      <div className="p-6 lg:p-8">
        <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Getting started</div>
        <h2 className="font-serif text-2xl lg:text-3xl tracking-tight">Welcome to Alteza Marketplace!</h2>
        <p className="text-sm text-muted-foreground mt-2">You’re just a few steps away from opening your store.</p>
        <div className="mt-5 flex items-center gap-3">
          <Progress value={pct} className="h-2 flex-1" data-testid="onboarding-progress" />
          <span className="text-sm font-medium tabular-nums whitespace-nowrap" data-testid="onboarding-progress-count">{completed}/{total} completed</span>
        </div>
      </div>
      <div className="divide-y divide-border border-t border-border">
        {tasks.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={t.go}
            data-testid={`onboarding-task-${t.key}`}
            className="w-full flex items-center gap-4 p-5 lg:px-8 text-left hover:bg-secondary/40 transition-colors group"
          >
            {t.done ? (
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" data-testid={`onboarding-task-${t.key}-done`} />
            ) : (
              <span className="w-6 h-6 rounded-full border border-border flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`font-medium text-base ${t.done ? "text-muted-foreground line-through" : ""}`}>{t.label}</div>
              {!t.done && <div className="text-sm text-muted-foreground mt-0.5">{t.desc}</div>}
            </div>
            {t.done ? (
              <span className="text-xs tracking-wide uppercase text-green-600 font-medium whitespace-nowrap">Done</span>
            ) : (
              <span
                data-testid={`onboarding-task-${t.key}-complete`}
                className="btn-primary px-4 py-2 text-sm rounded-sm inline-flex items-center gap-1.5 whitespace-nowrap"
              >
                Complete <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
